import { useEffect, useState } from 'react';
import { usePhone } from '@/hooks/usePhone';
import { FEEDBACK_KIND_LABEL, PAIN_SOURCE_LABEL, SAID_LABEL, feedbackWords, markToldUsRead, painEventFields, painWhere, sortToldUsUnreadFirst, type EveningNoteView, type PainEventView, type ProductFeedbackView, type ReadMark } from '@/lib/people-data';
import { day, when } from '@/lib/format';
import s from '@/pages/console/console.module.css';

/**
 * "What they told us": first every "stop, this hurts" Elina heard (pv-auth
 * #337 PainEvent), flagged, with what they were doing at the time and their
 * words exactly as said, never softened and never read as a diagnosis; then
 * what they said about the product (#334: Elina's question on the seventh
 * check day, and "Tell us what to improve") and their evening voice notes
 * (#326), each in full, unread first then newest. A recording the
 * transcriber could not read is said in the server's own words, never shown
 * as a blank row.
 *
 * Mark read on a feedback row or an evening note goes through
 * POST /provider/admin/people/:id/feedback/:feedbackId/read (#337), which
 * answers with the mark; the row updates from that answer. A row without an
 * id (an API older than #337) has no read state and no button.
 *
 * Above 640px each list is a table; under it one card per entry.
 */
type Readable = { id?: string; readAt?: string | null; readBy?: string | null; at: string };

function useReadMarks<T extends Readable>(list: T[]) {
  const [rows, setRows] = useState<T[]>(() => sortToldUsUnreadFirst(list));
  useEffect(() => { setRows(sortToldUsUnreadFirst(list)); }, [list]);
  const apply = (mark: ReadMark) => setRows((cur) => sortToldUsUnreadFirst(cur.map((r) => (r.id === mark.id ? { ...r, readAt: mark.readAt, readBy: mark.readBy } : r))));
  return { rows, apply };
}

export function ToldUs({ feedback, notes, painEvents, userId, onRead }: {
  feedback: ProductFeedbackView[];
  notes: EveningNoteView[];
  /** Undefined until pv-auth #337 is deployed; the section then says so rather than showing an empty list. */
  painEvents?: PainEventView[];
  userId: string;
  /** After the server confirmed a read, so the caller can refresh counts. */
  onRead?: (mark: ReadMark) => void;
}) {
  const phone = usePhone();
  const fb = useReadMarks(feedback);
  const ev = useReadMarks(notes);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const read = async (id: string) => {
    setBusy(id);
    setError(null);
    try {
      const mark = await markToldUsRead(userId, id);
      fb.apply(mark);
      ev.apply(mark);
      onRead?.(mark);
    } catch (err) {
      setError(`Could not mark it read: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const readWords = (r: Readable) => (r.readAt ? `read ${when(r.readAt)}${r.readBy ? ` by ${r.readBy}` : ''}` : r.id ? 'unread' : 'no read state on this API yet');
  const readCell = (r: Readable) => (r.readAt ? <span className={s.sub}>{readWords(r)}</span> : r.id ? <span className={`${s.pill} ${s.pill_unread}`}>unread</span> : <span className={s.missing}>no read state on this API yet</span>);
  const readBtn = (r: Readable) => (!r.readAt && r.id ? <button type="button" className={s.btnGhost} disabled={busy === r.id} onClick={() => void read(r.id!)} data-action="mark-read">{busy === r.id ? 'Marking' : 'Mark read'}</button> : null);
  const pains = painEvents ?? [];

  if (fb.rows.length === 0 && ev.rows.length === 0 && pains.length === 0 && painEvents !== undefined) {
    return <div className={s.empty}>Nothing told us yet: no pain stop, no seventh-check answer, no "what to improve", no evening note.</div>;
  }

  return (
    <div className={s.stack}>
      {error && <div className={s.error}>{error}</div>}

      <div data-panel="pain-stops">
        <div className={s.groupTitle}>Stopped because it hurt</div>
        {painEvents === undefined && <div className={s.notice}>Not on this API yet: pain stops need pv-auth #337 deployed.</div>}
        {painEvents !== undefined && pains.length === 0 && <div className={s.empty}>Never said it hurt.</div>}
        {pains.length > 0 && (phone ? (
          <div className={s.msgRows} data-panel="pain-cards">
            {pains.map((e, i) => {
              const f = painEventFields(e);
              return (
                <article key={f.id || i} className={`${s.msgRow} ${s.msgRowFlag}`} data-pain={f.id} data-flagged="1">
                  <div className={s.msgRowHead}>
                    <span className={`${s.pill} ${s.pill_flag}`}>flagged</span>
                    <span className={s.jTime}>{when(f.at)}</span>
                  </div>
                  <div className={s.painWhere}>{painWhere(e)} · {(PAIN_SOURCE_LABEL as Record<string, string>)[f.source] ?? f.source}</div>
                  <div className={s.msgText}>{f.transcript}</div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className={s.tableWrap} data-panel="pain-table">
            <table className={s.table}>
              <thead><tr><th>When</th><th>What they were doing</th><th>Their words</th><th>Heard by</th></tr></thead>
              <tbody>
                {pains.map((e, i) => {
                  const f = painEventFields(e);
                  return (
                    <tr key={f.id || i} className={s.rowFlag} data-pain={f.id} data-flagged="1">
                      <td><span className={`${s.pill} ${s.pill_flag}`}>flagged</span><div className={s.sub}>{when(f.at)}</div></td>
                      <td className="wrap"><span className={s.painWhere}>{painWhere(e)}</span></td>
                      <td className={s.msgCell}><div className={s.msgText}>{f.transcript}</div></td>
                      <td className={s.sub}>{(PAIN_SOURCE_LABEL as Record<string, string>)[f.source] ?? f.source}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <div>
        <div className={s.groupTitle}>About the product</div>
        {fb.rows.length === 0 && <div className={s.empty}>Nothing yet.</div>}
        {fb.rows.length > 0 && (phone ? (
          <div className={s.msgRows} data-panel="feedback-cards">
            {fb.rows.map((f, i) => {
              const w = feedbackWords(f);
              return (
                <article key={f.id ?? `${f.at}-${i}`} className={`${s.msgRow} ${f.readAt || !f.id ? '' : s.msgRowUnread}`} data-feedback={f.id} data-feedback-kind={f.kind} data-transcribed={w.transcribed ? '1' : '0'} data-unread={f.id ? (f.readAt ? '0' : '1') : undefined}>
                  <div className={s.msgRowHead}>
                    <span className={s.jKind}>{FEEDBACK_KIND_LABEL[f.kind]}</span>
                    <span className={s.sub}>{SAID_LABEL[f.source]}{f.audioSeconds !== null ? ` · ${f.audioSeconds} s` : ''}</span>
                    <span className={s.jTime}>{when(f.at)}</span>
                  </div>
                  <div className={`${s.msgText} ${w.transcribed ? '' : s.missing}`}>{w.text}</div>
                  <div className={s.sub}>{readWords(f)}</div>
                  {readBtn(f) && <div className={s.msgActions}>{readBtn(f)}</div>}
                </article>
              );
            })}
          </div>
        ) : (
          <div className={s.tableWrap} data-panel="feedback-table">
            <table className={s.table}>
              <thead><tr><th>When</th><th>Asked</th><th>Said</th><th>Their words</th><th>Read</th><th></th></tr></thead>
              <tbody>
                {fb.rows.map((f, i) => {
                  const w = feedbackWords(f);
                  return (
                    <tr key={f.id ?? `${f.at}-${i}`} className={f.readAt || !f.id ? '' : s.rowUnread} data-feedback={f.id} data-feedback-kind={f.kind} data-transcribed={w.transcribed ? '1' : '0'} data-unread={f.id ? (f.readAt ? '0' : '1') : undefined}>
                      <td>{when(f.at)}</td>
                      <td><span className={s.jKind}>{FEEDBACK_KIND_LABEL[f.kind]}</span></td>
                      <td className={s.sub}>{SAID_LABEL[f.source]}{f.audioSeconds !== null ? ` · ${f.audioSeconds} s` : ''}</td>
                      <td className={s.msgCell}><div className={`${s.msgText} ${w.transcribed ? '' : s.missing}`}>{w.text}</div></td>
                      <td>{readCell(f)}</td>
                      <td>{readBtn(f)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <div>
        <div className={s.groupTitle}>Evening notes</div>
        {ev.rows.length === 0 && <div className={s.empty}>No evening note yet.</div>}
        {ev.rows.length > 0 && (phone ? (
          <div className={s.msgRows} data-panel="evening-cards">
            {ev.rows.map((n, i) => (
              <article key={n.id ?? `${n.at}-${i}`} className={`${s.msgRow} ${n.readAt || !n.id ? '' : s.msgRowUnread}`} data-note={n.id} data-evening-day={n.day} data-unread={n.id ? (n.readAt ? '0' : '1') : undefined}>
                <div className={s.msgRowHead}>
                  <span className={s.jKind}>{day(n.day)}</span>
                  <span className={s.sub}>{SAID_LABEL[n.source]}{n.audioSeconds !== null ? ` · ${n.audioSeconds} s` : ''} · {n.timeZone}</span>
                  <span className={s.jTime}>{when(n.at)}</span>
                </div>
                <div className={s.msgText}>{n.transcript}</div>
                <div className={s.sub}>{readWords(n)}</div>
                {readBtn(n) && <div className={s.msgActions}>{readBtn(n)}</div>}
              </article>
            ))}
          </div>
        ) : (
          <div className={s.tableWrap} data-panel="evening-table">
            <table className={s.table}>
              <thead><tr><th>Day</th><th>Left at</th><th>Said</th><th>Their words</th><th>Read</th><th></th></tr></thead>
              <tbody>
                {ev.rows.map((n, i) => (
                  <tr key={n.id ?? `${n.at}-${i}`} className={n.readAt || !n.id ? '' : s.rowUnread} data-note={n.id} data-evening-day={n.day} data-unread={n.id ? (n.readAt ? '0' : '1') : undefined}>
                    <td>{day(n.day)}<div className={s.sub}>{n.timeZone}</div></td>
                    <td>{when(n.at)}</td>
                    <td className={s.sub}>{SAID_LABEL[n.source]}{n.audioSeconds !== null ? ` · ${n.audioSeconds} s` : ''}</td>
                    <td className={s.msgCell}><div className={s.msgText}>{n.transcript}</div></td>
                    <td>{readCell(n)}</td>
                    <td>{readBtn(n)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
